import { type ConfluenceComment } from '@canvas/atlassian-service/confluence/types';
import { currentUserState } from '@canvas/canvas-state/user';

import { type CommentModel } from '../model';

export type OptimisticWriter<T = unknown> = (
  comments: CommentModel[],
  comment: CommentModel | undefined,
  meta: T
) => CommentModel[];

export type PersistentWriter = (
  comments: CommentModel[],
  comment: CommentModel | undefined,
  result: ConfluenceComment
) => CommentModel[];

export const optimisticAdd: OptimisticWriter<{
  content: string;
  threadId: string;
  optimisticId: string;
  type: 'root' | 'reply';
}> = (comments, _comment, meta) => {
  if (currentUserState.status !== 'succeeded') {
    return comments;
  }

  const user = currentUserState.identity;

  const commentOptimistic: CommentModel = {
    id: meta.optimisticId,
    reactionsId: meta.optimisticId,
    createdAt: new Date().toISOString(),
    isOptimistic: true,
    type: meta.type,
    threadId: meta.threadId,
    body: {
      value: meta.content,
    },
    author: {
      // @todo: Extract and localise as part of public links stream
      // https://product-fabric.atlassian.net/browse/CNS-9315
      displayName: user.displayName ?? 'Anonymous',
      profilePicture: {
        path: user.picture,
      },
      accountId: user.accountId,
    },
    version: {
      friendlyWhen: 'Saving...',
      number: 0,
    },
    reactions: [],
    permissions: { isEditable: false, isRemovable: false },
    replies: [],
    resolved: false,
  };

  comments.push(commentOptimistic);

  return comments;
};

export const persistentAdd: PersistentWriter = (comments, comment, result) => {
  if (!comment) {
    return comments;
  }

  comment.id = result.id;
  comment.reactionsId = result.reactionsId;
  comment.isOptimistic = false;
  comment.version = {
    friendlyWhen: result.version.friendlyWhen,
    number: result.version.number,
  };
  comment.permissions = result.permissions;
  comment.createdAt = result.createdAtNonLocalized;

  return comments;
};

export const optimisticUpdate: OptimisticWriter<{ content: string }> = (
  comments,
  comment,
  meta
) => {
  if (!comment) {
    return comments;
  }

  const oldVersion = comment.version?.number ?? 1;
  const newVersion = oldVersion + 1;

  comment.body = { value: meta.content };
  comment.version = {
    friendlyWhen: 'Saving...',
    number: newVersion,
  };

  return comments;
};

export const persistentUpdate: PersistentWriter = (comments, comment, result) => {
  if (!comment) {
    return comments;
  }

  comment.version = {
    friendlyWhen: result.version.friendlyWhen,
    number: result.version.number,
  };

  comment.permissions = result.permissions;

  return comments;
};

export const optimisticDelete: OptimisticWriter = (comments, comment) => {
  return comments.filter((c) => c.id !== comment?.id);
};

/**
 * Performs the operation we expect to be performed by the persistent resolve
 * comment call
 * @param comments the comments in the thread
 * @param comment the comment we are resolving
 * @returns the new thread
 */
export const optimisticResolve: OptimisticWriter = (comments, comment) => {
  if (!comment) {
    return comments;
  }

  comment.resolved = true;

  return comments;
};

/**
 * Performs the operation we expect to be performed by the persistent re-open
 * comment call
 * @param comments the comments in the thread
 * @param comment the comment we are re-opening
 * @returns the new thread
 */
export const optimisticReOpen: OptimisticWriter = (comments, comment) => {
  if (!comment) {
    return comments;
  }

  comment.resolved = false;

  return comments;
};
